import { z } from "zod";
import { COUNTRY_ISO2_PATTERN } from "./airports";
import { paginationQuery, searchParam } from "./pagination";
import { CARRIER_CODE_PATTERN } from "./travel";

/**
 * `/airlines` — our airline list and its logos (migration 0087, user
 * 2026-11-03). Seeded from a curated list (`airlines:seed`), edited by
 * SUPER_ADMIN; logos live in OUR private bucket (`airlines/<logo id>/logo.png`),
 * fetched once by `airlines:logos` or uploaded by an admin, and are served as
 * presigned GETs on a floored window (like photos). A missing logo is
 * `logo_url: null` — the frontend draws a code badge.
 *
 * IATA designators are reused by new airlines after an old one folds, so the
 * code is unique among ACTIVE airlines only.
 */

/** An ICAO airline designator: `MAS`. `ck_airlines_icao_code`. */
export const AIRLINE_ICAO_PATTERN = /^[A-Z]{3}$/;
export const AIRLINE_NAME_MAX = 120;

/** What an admin may upload as a logo. Always stored as PNG (transparency kept), ≤ AIRLINE_LOGO_EDGE px on the long edge. */
export const AIRLINE_LOGO_UPLOAD_CONTENT_TYPES = ["image/png", "image/jpeg", "image/webp"] as const;
export type AirlineLogoUploadContentType = (typeof AIRLINE_LOGO_UPLOAD_CONTENT_TYPES)[number];
export const AIRLINE_LOGO_MAX_BYTES = 2 * 1024 * 1024;
export const AIRLINE_LOGO_EDGE = 256;

/** `GET /airlines/logos?codes=` — one call per screen, never per row. */
export const AIRLINE_LOGO_CODES_MAX = 50;

const iata = z.string().trim().toUpperCase().regex(CARRIER_CODE_PATTERN, "Must be a 2-character IATA airline code.");
const icao = z.string().trim().toUpperCase().regex(AIRLINE_ICAO_PATTERN, "Must be a 3-letter ICAO airline code.");
const country = z.string().trim().toUpperCase().regex(COUNTRY_ISO2_PATTERN, "Must be a 2-letter ISO country code.");
const name = z.string().trim().min(1).max(AIRLINE_NAME_MAX);
const reason = z.string().trim().min(1).max(300);

export const listAirlinesQuery = paginationQuery.extend({
	/** Code (IATA / ICAO, exact) or name (contains). */
	q: searchParam.optional(),
	has_logo: z.enum(["true", "false"]).optional(),
	is_active: z.enum(["true", "false"]).optional(),
});
export type ListAirlinesQuery = z.infer<typeof listAirlinesQuery>;

export const createAirlineBody = z.object({
	iata_code: iata,
	icao_code: icao.nullish(),
	name,
	country_code: country.nullish(),
	is_active: z.boolean().default(true),
	reason: reason.optional(),
});

export const updateAirlineBody = z
	.object({
		name: name.optional(),
		icao_code: icao.nullable().optional(),
		country_code: country.nullable().optional(),
		is_active: z.boolean().optional(),
		/** The version the editor loaded → 409 STALE_VERSION when it moved. */
		version: z.number().int().min(1),
		reason: reason.optional(),
	})
	.refine((b) => b.name !== undefined || b.icao_code !== undefined || b.country_code !== undefined || b.is_active !== undefined, {
		message: "Nothing to change.",
	});

/** `codes=TG,MH,AK`: trimmed, upper-cased, de-duplicated, 1..AIRLINE_LOGO_CODES_MAX valid designators. */
export const airlineLogosQuery = z.object({
	codes: z
		.string()
		.trim()
		.min(1)
		.max(AIRLINE_LOGO_CODES_MAX * 4)
		.transform((s) => [...new Set(s.split(",").map((c) => c.trim().toUpperCase()).filter((c) => c !== ""))])
		.pipe(z.array(z.string().regex(CARRIER_CODE_PATTERN)).min(1).max(AIRLINE_LOGO_CODES_MAX)),
});

export const airlineLogoUploadBody = z.object({
	content_type: z.enum(AIRLINE_LOGO_UPLOAD_CONTENT_TYPES),
	size_bytes: z.number().int().min(1),
});

export const airlineLogoCompleteBody = z.object({ upload_token: z.string().min(1).max(4000) });

/** One row of `GET /airlines` (and `GET` / `PATCH` / `POST` of one). */
export interface AirlineItem {
	airline_id: string;
	iata_code: string;
	icao_code: string | null;
	name: string;
	country_code: string | null;
	is_active: boolean;
	/** Presigned GET on a floored window (cached across a list), null = no logo: draw the code badge. */
	logo_url: string | null;
	logo_bytes: number | null;
	logo_updated_at: string | null;
	version: number;
	created_at: string;
	updated_at: string;
}

/** One row of `GET /airlines/logos`: a requested code with no active airline is simply absent. */
export interface AirlineLogo {
	iata_code: string;
	name: string;
	logo_url: string | null;
}

/** `POST /airlines/:id/logo/upload-url`: PUT the bytes to `upload_url` with `upload_headers`, then complete with `upload_token`. */
export interface AirlineLogoUploadStart {
	upload_url: string;
	upload_headers: Record<string, string>;
	upload_token: string;
	expires_at: string;
}
